import { Collapse, DatePicker, Divider, Input, Tooltip } from "antd";
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChantInput, ChantSelect, ChantCheckbox } from "../../../../../CommonComponents";
import ImageUploaderButton from "../../../website/ImageUploaderButton";
import PackageHistory from "../PackagesTable/PackageHistory";

// Import new modular components and utilities
import {
  statusDataList,
  shippingOptions,
} from "../../constants/memberPackageConstants";
import { getSeasonList } from "./utils/seasonUtils";
import { useMemberPackageForm } from "./useMemberPackageForm";
import PriceInput from "./PriceInput";
import ChildPriceInput from "./ChildPriceInput";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import useCurrency from "../../../../../utils/hooks/useCurrency";
import MerchandiseSection from "./MerchandiseSection/MerchandiseSection";
import ChantContainer from "../../../../../CommonComponents/ChantContainer";
import ScreenHeader from "../../../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";
import DescriptionText from "../../../../../CommonComponents/DescriptionText";

const PackageForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const packageId = params.id;
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const handleClose = () => {
    navigate("/admin/members/packages");
  };

  const { state, updateState, handleDateChange, savePackage } =
    useMemberPackageForm(packageId, null, handleClose);

  const groupCurrency = useCurrency();

  const { Panel } = Collapse;

  return (
    <ChantContainer>
      <ScreenHeader
        title={packageId ? "Edit Package" : "Add Package"}
        onBackClick={handleClose}
      />

      <div style={{ marginTop: "var(--screen-general-margin)" }}>
        <DescriptionText showBackground>
          <p>
            Members can only buy one package per email per season. <br />{" "}
            <b>Important:</b> Ensure members renew with the same email or it
            will not update their profile and Chant access. <br /> Ensure
            subscription &apos;Stripe&apos; email settings are managed (see
            Packages / Setup for details). <br /> All subscriptions auto-renew
            on the package expiration date. Do not change the package expiration
            date (for the same season) without engaging Chant support.
            <br />
            <b>Add/Edit Package:</b> Groups offering auto-renewal should reuse
            the same package each season. Changing the &apos;season&apos; will
            create a new &apos;version&apos; of the package (see version history
            below) and must include a new package expiration date. <br /> Reach
            out to support@chant.fan when managing subscriptions.
          </p>
        </DescriptionText>
      </div>
      <div
        style={{
          padding: "12px",
          marginTop: "var(--screen-general-margin)",
          backgroundColor: "var(--primary-white-background)",
        }}
      >
        <div className="package-form-container">
          <div style={{ marginTop: "20px" }}>
            <div className="chant-form-body">
              <ChantSelect
                label="Status"
                required
                value={state.status}
                onChange={(value) => {
                  updateState({ status: value });
                }}
                options={statusDataList.map((item) => ({
                  value: item.key,
                  label: item.value,
                }))}
              />
              <ChantSelect
                label="Season"
                required
                value={state.season}
                onChange={(value) => {
                  if (packageId) {
                    let isConfirmed = window.confirm(
                      "Warning: If updating the package for the following season you should update both the package expiration date and the season. Members can purchase one package per season.",
                    );
                    if (isConfirmed) updateState({ season: value });
                  } else {
                    updateState({ season: value });
                  }
                }}
                options={getSeasonList(
                  groupData?.leagueId,
                  state.originalPkgSeason,
                ).map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
              <ChantInput
                label="Package Name"
                required
                value={state.name}
                name="name"
                onChange={(e) => {
                  let editChanges = state.editChanges;
                  editChanges.nameChange = true;
                  editChanges.productId = state.stripeProductId;
                  updateState({
                    name: e.target.value,
                    editChanges: editChanges,
                  });
                }}
                maxLength={50}
              />
              <div style={{ marginBottom: "16px" }}>
                <label>Description</label>
                <Input.TextArea
                  value={state.desc}
                  name="desc"
                  onChange={(e) => {
                    updateState({ desc: e.target.value });
                  }}
                  maxLength={500}
                  rows={2}
                />
              </div>
              <div style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                  <label style={{ marginBottom: 0 }}>
                    Package Expiration Date<span style={{ color: "red" }}>*</span>
                  </label>
                  <Tooltip
                    placement="topLeft"
                    title={
                      <div style={{ width: 400 }}>
                        Important: After this date, members who have not
                        renewed will be locked out of Chant. Include a
                        &apos;grace period&apos; allowing time to renew
                        after your next membership drive begins.
                      </div>
                    }
                  >
                    <span className="border-0">
                      &nbsp;<i className="fa fa-question-circle-o"></i>
                    </span>
                  </Tooltip>
                </div>
                <DatePicker
                  tabIndex="2"
                  value={state.packageExpiryDate}
                  onChange={(value) => {
                    if (packageId) {
                      let isConfirmed = window.confirm(
                        "Warning: Do not edit the expiration date for packages using auto-renewal without engaging Chant support. If updating the package for the following season you should update both the package expiration date AND the season (new package version is created).",
                      );
                      if (isConfirmed) {
                        handleDateChange(
                          "packageExpiryDate",
                          value,
                        );
                      }
                    } else {
                      handleDateChange(
                        "packageExpiryDate",
                        value,
                      );
                    }
                  }}
                />
                <div>
                  Package expires on this date (if not on auto-renewal).
                </div>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label>Offer auto-renewal?</label>
                <div>
                  <ChantCheckbox
                    checked={state.offerAutorenewal}
                    onChange={(e) => {
                      updateState({
                        offerAutorenewal: e.target.checked,
                      });
                    }}
                    tabIndex="2"
                  >
                    <span>
                      If selected members will be offered option to
                      auto-renew the package.
                    </span>
                  </ChantCheckbox>
                </div>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label>Image<span style={{ color: "red" }}>*</span></label>
                <div>
                  <ImageUploaderButton
                    text="Upload Image"
                    images={[state.image]}
                    onSave={(images) => {
                      updateState({
                        image: images[0],
                      });
                    }}
                    aspectRatio={1}
                  />
                </div>
              </div>
              <PriceInput
                price={state.price}
                onPriceChange={(newPrice, newEditChanges) => {
                  updateState({
                    price: newPrice,
                    editChanges: newEditChanges,
                  });
                }}
                onTypeChange={(e) => {
                  let price = state.price;
                  price.type = e.target.value;
                  let updatedState = { price };
                  if (
                    price.type === "Per family" ||
                    price.type === "Per member"
                  ) {
                    updatedState.childPrice = {
                      amount: 0,
                      shipping: 0,
                    };
                    updatedState.includesChildPrice = false;
                  }
                  updateState(updatedState);
                }}
                viewMode={state.viewMode}
                editChanges={state.editChanges}
                stripeProductId={state.stripeProductId}
              />
              {state.includesChildPrice ? (
                <ChildPriceInput
                  childPrice={state.childPrice}
                  onChildPriceChange={(newChildPrice, newEditChanges) => {
                    updateState({
                      childPrice: newChildPrice,
                      editChanges: newEditChanges,
                    });
                  }}
                  onRemove={() => {
                    updateState({
                      childPrice: {
                        amount: 0,
                        shipping: 0,
                      },
                      includesChildPrice: false,
                    });
                  }}
                  editChanges={state.editChanges}
                />
              ) : (
                state.price.type !== "Per family" &&
                state.price.type !== "Per member" && (
                  <div style={{ marginBottom: "16px" }}>
                    <ChantButton
                      type="secondary"
                      onClick={() => updateState({ includesChildPrice: true })}
                    >
                      + Add price per child
                    </ChantButton>
                  </div>
                )
              )}
              <ChantSelect
                label="Offer shipping"
                required
                placeholder="Offer shipping..."
                value={state.offerShipping}
                disabled={state.viewMode}
                onChange={(val) => {
                  updateState({ offerShipping: val });
                }}
                options={shippingOptions}
              />
              <ChantInput
                label="Shipping note"
                value={state.shippingNote}
                name="shippingNote"
                onChange={(e) => {
                  updateState({ shippingNote: e.target.value });
                }}
                maxLength={100}
              />
              <ChantInput
                label="Pickup note"
                value={state.pickupNote}
                name="pickupNote"
                onChange={(e) => {
                  updateState({ pickupNote: e.target.value });
                }}
                maxLength={100}
              />
              <MerchandiseSection
                productChoice={state.productChoice}
                onProductChoiceChange={(e) => {
                  updateState({
                    productChoice: e.target.value,
                  });
                }}
                products={state.products}
                onProductsChange={(newProducts) => {
                  updateState({ products: newProducts });
                }}
                storeItems={state.storeItems}
              />
            </div>

            {Object.keys(state?.packageTimeline || {}).length > 0 && (
              <div style={{ marginTop: "32px" }}>
                <Collapse defaultActiveKey={["1"]}>
                  <Panel header="Package History" key="1">
                    <PackageHistory
                      timeline={state.packageTimeline}
                      groupCurrency={groupCurrency}
                      storeItems={state.storeItems}
                    />
                  </Panel>
                </Collapse>
              </div>
            )}

            <Divider />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                marginTop: "20px",
              }}
            >
              <ChantButton
                type="secondary"
                onClick={handleClose}
                disabled={state.saving}
              >
                Cancel
              </ChantButton>
              <ChantButton
                onClick={() => {
                  savePackage();
                }}
                loading={state.saving}
                disabled={state.saving}
              >
                {state.saving ? "Submitting..." : "Submit"}
              </ChantButton>
            </div>
          </div>
        </div>
      </div>
    </ChantContainer>
  );
};

export default PackageForm;
